import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { updateProfile } from "../../actions/authActions";
import axios from "axios";
import { apiUrl } from "../../config/config";

import SideBar from "../../components/common/SideBar";
import AttendanceView from "../Verifier-dashboard/components/AttendanceView";
import { performLogout } from "../../utils/logout";
import "./VerifierProfile.css";

// Inline-style "Log Out" button shared by every profile page so the
// option is always visible and looks identical across roles. Uses the
// same `performLogout` helper SideBar uses, so the close-out behavior
// (verifier-attendance API + activity flush + redirect) stays in sync.
const ProfileLogoutButton = () => (
  <button
    type="button"
    onClick={(e) => {
      if (e && e.preventDefault) e.preventDefault();
      performLogout();
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 18px",
      borderRadius: 8,
      border: "1px solid rgba(239,68,68,0.35)",
      background: "rgba(239,68,68,0.10)",
      color: "#ff6b6b",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background 0.15s ease, border-color 0.15s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "rgba(239,68,68,0.18)";
      e.currentTarget.style.borderColor = "rgba(239,68,68,0.55)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "rgba(239,68,68,0.10)";
      e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)";
    }}
  >
    <span aria-hidden="true">🚪</span> Log Out
  </button>
);

// Same `report_status` interpretation used elsewhere in the app:
//   0 NotAnalyzed · 1 Analyzing · 2 Analyzed · 3 VerificationCompleted
//   4 InProgress · 5 OnHold · 6 ReportGenerated · 7 Delivered
function statusLabel(st) {
  var n = Number(st);
  if (n === 0) return "Not analyzed";
  if (n === 1) return "Analyzing";
  if (n === 2) return "Analyzed";
  if (n === 3) return "Verified";
  if (n === 4) return "In Preparation";
  if (n === 5) return "On Hold";
  if (n === 6) return "Report Generated";
  if (n === 7) return "Delivered";
  return "—";
}

// Show `1d` / `2d` from a possibly-in-hours field — same logic as
// AssignPatients.normalizeMonitoringDays but inlined here so this file
// stays self-contained.
function asDays(raw) {
  var n = Number(raw);
  if (isNaN(n) || n <= 0) return null;
  if (n >= 1 && n <= 14) return Math.round(n);
  if (n >= 24 && n % 24 === 0) {
    var d = n / 24;
    if (d >= 1 && d <= 14) return d;
  }
  if (n > 14) {
    var r = Math.round(n / 24);
    if (r >= 1 && r <= 14) return r;
  }
  return null;
}

class VerifierProfile extends Component {
  constructor() {
    super();
    this.state = {
      // Sidebar menu — Patient list / Verifier home / Verified / QC
      menus: [
        { title: "Patient List", url: "/patient-list" },
        { title: "Verifier Home", url: "/verifier-home" },
        { title: "Verified", url: "/Verification-Completed" },
        { title: "QC", url: "/qc_home" },
      ],
      // Restricted menu for verifiertype === "7" (kept compatible with other pages)
      menu: [{ title: "Patient List", url: "/Partner" }],
      profileLink: "verifier-profile",
      name: "",
      phone: "",
      email: "",
      gender: "M",
      address: "",
      errors: {},
      saving: false,
      savedAt: null,

      // ── Self-service file request ───────────────────────────────
      reqCount:        5,       // how many patients to grab
      reqNotes:        "",      // optional free-text notes
      requesting:      false,   // POST in flight
      reqError:        "",
      reqFlash:        "",      // green message after a successful request

      // ── Currently-assigned patient list ─────────────────────────
      assigned:        [],      // [{_id, name, device_id, …}]
      assignedLoading: false,
      assignedError:   "",
    };
  }

  componentDidMount() {
    axios
      .get(apiUrl + "api/users/profile")
      .then((res) => {
        this.setState({
          name: res.data.name || "",
          phone: res.data.phone || "",
          email: res.data.email || "",
          gender: res.data.gender || "M",
          address: res.data.address || "",
          id: res.data._id,
        });
      })
      .catch(() => {});

    // Also load the verifier's currently-assigned patient list.
    this.loadAssigned();
  }

  // Fetch all patients currently assigned to me. Backend route is
  // GET /api/users/myAssignedPatients — uses the JWT to identify
  // the caller, so no params needed.
  loadAssigned = () => {
    this.setState({ assignedLoading: true, assignedError: "" });
    axios
      .get(apiUrl + "api/users/myAssignedPatients?_t=" + Date.now(), {
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      })
      .then((res) => {
        var data = res.data || {};
        var list = Array.isArray(data.patients) ? data.patients
                 : Array.isArray(data) ? data : [];
        this.setState({ assigned: list, assignedLoading: false });
      })
      .catch((err) => {
        this.setState({
          assignedLoading: false,
          assignedError:
            (err.response && err.response.data && err.response.data.error)
            || err.message || "Failed to load assignments",
        });
      });
  };

  // Submit a self-service file request. Backend grabs the oldest N
  // unassigned, in-queue patients and adds them to this verifier's
  // `patients` array, then returns the assigned rows so the UI can
  // append them without a second round-trip.
  requestFiles = async () => {
    var count = Math.max(1, Math.min(20, Number(this.state.reqCount) || 5));
    var notes = String(this.state.reqNotes || "").slice(0, 500);
    this.setState({ requesting: true, reqError: "", reqFlash: "" });
    try {
      var res = await axios.post(apiUrl + "api/users/requestAssignment", {
        count: count,
        notes: notes,
      });
      var data = res.data || {};
      var assigned = Array.isArray(data.assigned) ? data.assigned : [];
      if (!assigned.length) {
        this.setState({
          requesting: false,
          reqFlash:    "",
          reqError:    data.message || "No unassigned patients available right now.",
        });
        return;
      }
      this.setState({
        requesting: false,
        reqFlash:
          (data.message || "Assigned")
          + " — " + assigned.length + " patient" + (assigned.length === 1 ? "" : "s")
          + " added to your queue.",
        reqNotes: "",
      });
      // Pull the fresh list so it shows what's actually persisted.
      this.loadAssigned();
    } catch (err) {
      this.setState({
        requesting: false,
        reqError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Request failed.",
      });
    }
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.errors) {
      this.setState({ errors: nextProps.errors });
    }
  }

  onChange = (e) => {
    const err = { ...this.state.errors };
    delete err[e.target.name];
    this.setState({ [e.target.name]: e.target.value, errors: err });
  };

  onSubmit = (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    const user = {
      id: this.state.id,
      name: this.state.name,
      phone: this.state.phone,
      email: this.state.email,
      gender: this.state.gender,
      address: this.state.address,
    };
    this.props.updateProfile(user, this.props.history);
    // Optimistic feedback — the action is fire-and-forget and may navigate.
    setTimeout(() => {
      this.setState({ saving: false, savedAt: new Date() });
    }, 600);
  };

  render() {
    const { errors, name, email, phone, address, gender, saving, savedAt } =
      this.state;

    return (
      <div className="vp-page">
        <SideBar
          menus={
            localStorage.getItem("verifiertype") !== "7"
              ? this.state.menus
              : this.state.menu
          }
        />

        <div className="page-wrapper">
          <div className="vp-container">
            {/* Header — title + Log Out (visible regardless of sidebar
                layout / clipping). */}
            <div
              className="vp-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h2>Profile</h2>
                <p>Attendance, verified activity and personal details</p>
              </div>
              <ProfileLogoutButton />
            </div>

            {/* ── Attendance & Verified Activity (primary) ────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>My Attendance & Verified Activity</h3>
                <span className="vp-card__hint">
                  Login / break / files & patients verified — Today, Week,
                  Month, or Custom range
                </span>
              </div>
              <AttendanceView selfOnly />
            </div>

            {/* ── Request files (self-service auto-pick) ─────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Request file assignment</h3>
                <span className="vp-card__hint">
                  Click Request to pull the oldest unassigned files into your queue.
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <label className="vp-field" style={{ minWidth: 120, maxWidth: 140 }}>
                  <span className="vp-field__label">How many?</span>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={this.state.reqCount}
                    onChange={(e) =>
                      this.setState({ reqCount: e.target.value })
                    }
                    className="vp-input"
                  />
                </label>

                <label className="vp-field" style={{ flex: 1, minWidth: 220 }}>
                  <span className="vp-field__label">Notes (optional)</span>
                  <input
                    type="text"
                    value={this.state.reqNotes}
                    maxLength={500}
                    onChange={(e) =>
                      this.setState({ reqNotes: e.target.value })
                    }
                    placeholder="e.g. give me 1-day files"
                    className="vp-input"
                  />
                </label>

                <div className="vp-actions" style={{ paddingTop: 22 }}>
                  <button
                    type="button"
                    className="vp-btn"
                    onClick={this.requestFiles}
                    disabled={this.state.requesting}
                  >
                    {this.state.requesting ? "Requesting…" : "Request files"}
                  </button>
                </div>
              </div>

              {this.state.reqFlash && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(34,197,94,0.10)",
                    border: "1px solid rgba(34,197,94,0.35)",
                    color: "#22c55e",
                    fontSize: 13,
                  }}
                >✓ {this.state.reqFlash}</div>
              )}
              {this.state.reqError && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.35)",
                    color: "#ff6b6b",
                    fontSize: 13,
                  }}
                >{this.state.reqError}</div>
              )}
            </div>

            {/* ── My assigned patients ───────────────────────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>My assigned patients</h3>
                <span className="vp-card__hint">
                  {this.state.assignedLoading
                    ? "Loading…"
                    : this.state.assigned.length
                      + " patient" + (this.state.assigned.length === 1 ? "" : "s")
                      + " in your queue"}
                  {" · "}
                  <button
                    type="button"
                    onClick={this.loadAssigned}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#60a5fa",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: "inherit",
                    }}
                  >↻ refresh</button>
                </span>
              </div>

              {this.state.assignedError && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.35)",
                    color: "#ff6b6b",
                    fontSize: 13,
                  }}
                >{this.state.assignedError}</div>
              )}

              {!this.state.assignedLoading && !this.state.assigned.length && (
                <div style={{ padding: "16px 0", opacity: 0.7, fontSize: 14 }}>
                  No patients in your queue yet. Click "Request files" above to grab some from the queue.
                </div>
              )}

              {this.state.assigned.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", opacity: 0.75 }}>
                        <th style={{ padding: "8px 6px" }}>#</th>
                        <th style={{ padding: "8px 6px" }}>Patient</th>
                        <th style={{ padding: "8px 6px" }}>Device id</th>
                        <th style={{ padding: "8px 6px" }}>Customer</th>
                        <th style={{ padding: "8px 6px" }}>Duration</th>
                        <th style={{ padding: "8px 6px" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.assigned.map((r, i) => {
                        var d = asDays(r.exp_device_use_days);
                        return (
                          <tr
                            key={r._id || i}
                            style={{
                              borderTop: "1px solid rgba(255,255,255,0.06)",
                            }}
                          >
                            <td style={{ padding: "8px 6px", opacity: 0.6 }}>{i + 1}</td>
                            <td style={{ padding: "8px 6px" }}>{r.name || "—"}</td>
                            <td style={{ padding: "8px 6px", fontFamily: "monospace" }}>{r.device_id || "—"}</td>
                            <td style={{ padding: "8px 6px" }}>{r.customerName || "—"}</td>
                            <td style={{ padding: "8px 6px" }}>{d ? d + "d" : "—"}</td>
                            <td style={{ padding: "8px 6px" }}>{statusLabel(r.report_status)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Personal details (secondary, below attendance) ──── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Personal Details</h3>
                {savedAt && (
                  <span className="vp-saved">
                    Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>

              <form onSubmit={this.onSubmit} className="vp-form" autoComplete="off">
                <div className="vp-grid">
                  <label className="vp-field">
                    <span className="vp-field__label">Name</span>
                    <input
                      type="text"
                      name="name"
                      required
                      onChange={this.onChange}
                      value={name}
                      placeholder="Full name"
                      className={
                        "vp-input" + (errors.name ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Email</span>
                    <input
                      type="email"
                      name="email"
                      required
                      onChange={this.onChange}
                      value={email}
                      placeholder="email@example.com"
                      className={
                        "vp-input" + (errors.email ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Mobile</span>
                    <input
                      type="tel"
                      name="phone"
                      required
                      onChange={this.onChange}
                      value={phone}
                      placeholder="Phone"
                      className={
                        "vp-input" + (errors.phone ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Gender</span>
                    <div className="vp-radio-row">
                      <label className="vp-radio">
                        <input
                          type="radio"
                          name="gender"
                          value="M"
                          checked={gender === "M"}
                          onChange={this.onChange}
                        />
                        <span>Male</span>
                      </label>
                      <label className="vp-radio">
                        <input
                          type="radio"
                          name="gender"
                          value="F"
                          checked={gender === "F"}
                          onChange={this.onChange}
                        />
                        <span>Female</span>
                      </label>
                    </div>
                  </label>

                  <label className="vp-field vp-field--wide">
                    <span className="vp-field__label">Address</span>
                    <textarea
                      name="address"
                      onChange={this.onChange}
                      value={address}
                      rows={3}
                      placeholder="Address"
                      className="vp-input vp-input--multiline"
                    />
                  </label>
                </div>

                <div className="vp-actions">
                  <button
                    type="submit"
                    className="vp-btn"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

VerifierProfile.propTypes = {
  updateProfile: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  errors: state.errors,
});

export default connect(mapStateToProps, { updateProfile })(
  withRouter(VerifierProfile)
);
